import type {
  DomainPropertyIR,
  DomainPropertySummary,
  ManifestSearchableInput,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

type NormalizedSearchableConfig = {
  enabled: boolean | null;
  phonetic: boolean;
};

function supportsDefaultSearchable(scalarType?: string): boolean {
  return String(scalarType || "").toLowerCase() === "string";
}

function normalizeSearchableInput(
  searchable: boolean | ManifestSearchableInput | undefined,
  scalarType?: string,
): NormalizedSearchableConfig | null {
  if (!supportsDefaultSearchable(scalarType)) return null;

  if (typeof searchable === "boolean" || searchable === undefined) {
    return {
      enabled: searchable !== false,
      phonetic: false,
    };
  }

  const enabled = searchable.enabled !== false;
  return {
    enabled,
    phonetic: enabled && searchable.phonetic === true,
  };
}

export function buildSearchableFieldMutations(
  fieldPath: string,
  searchable: boolean | ManifestSearchableInput | undefined,
  scalarType?: string,
) {
  const normalized = normalizeSearchableInput(searchable, scalarType);
  if (normalized === null) return [];

  return [
    {
      label: `set searchable on ${fieldPath}`,
      query: `mutation { at(path: ${JSON.stringify(fieldPath)}) { set_searchable(enabled: ${normalized.enabled}, phonetic: ${normalized.phonetic}) { enabled phonetic } } }`,
    },
  ];
}

export const searchableMetamodelPackage = defineMetamodelPackage({
  id: "searchable",
  docs: {
    fieldRows: [
      {
        key: "searchable",
        description:
          "Controls full-text search exposure for string fields. String fields are searchable by default; set `searchable: false` to opt out, or use `searchable: { phonetic: true }` to keep the field searchable while enabling FalkorDB phonetic matching for class-wide search.",
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type SearchableMetamodel {
        model: Model!
        enabled: Boolean!
        phonetic: Boolean!
      }

      extend type Model {
        searchable: SearchableMetamodel
      }

      extend type ModelMutation {
        set_searchable(enabled: Boolean!, phonetic: Boolean): SearchableMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        SearchableMetamodel: {
          model: (value) => value.model,
          enabled: (value) => value.enabled !== false,
          phonetic: (value) => value.phonetic === true,
        },
        Model: {
          searchable: async (ant) => await run(ant.searchable()),
        },
        ModelMutation: {
          set_searchable: async (ant, { enabled, phonetic }) => {
            return {
              model: await run(ant.set_searchable(enabled, phonetic === true)),
              enabled,
              phonetic: phonetic === true,
            };
          },
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildSearchableFieldMutations(
        fieldPath,
        spec.searchable,
        spec.type,
      );
    },
  },
  summary: {
    selections: {
      propertyFields: [`searchable { enabled phonetic }`],
    },
    readPropertySummary(rawProperty) {
      if (typeof rawProperty.searchable?.enabled !== "boolean") {
        return {};
      }

      return {
        searchable: rawProperty.searchable.enabled,
        searchablePhonetic: rawProperty.searchable.phonetic === true,
      };
    },
  },
  domain: {
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      _propertySummary: DomainPropertySummary,
    ) {
      return propertyIR;
    },
  },
});
